import {
  ArrowUpRight,
  BriefcaseBusiness,
  CircleCheck,
  FileText,
  LayoutDashboard,
  Settings,
  ShieldCheck,
  SlidersHorizontal
} from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";
import { useActiveMotion } from "../hooks/useActiveMotion";
import { OffrLogo } from "./OffrLogo";

const modes = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "applications", label: "Applications", icon: BriefcaseBusiness },
  { id: "resumes", label: "Resumes", icon: FileText },
  { id: "settings", label: "Agent settings", icon: Settings }
] as const;

const applications = [
  ["Northstar", "Frontend Engineer", "Review"],
  ["Paperplane", "Product Engineer", "In queue"],
  ["Kernel Works", "Software Engineer", "Screening"],
  ["Daybreak", "UI Engineer", "Applied"],
  ["Monsoon Labs", "Full-stack Engineer", "Saved"]
] as const;

type Mode = (typeof modes)[number]["id"];

function OverviewPane() {
  return (
    <div className="dashboard-tour__overview">
      <div className="dashboard-tour__stats">
        <span><small>Chosen this week</small><strong>8</strong><i>+3 queued</i></span>
        <span><small>Awaiting review</small><strong>2</strong><i>needs you</i></span>
        <span><small>Status changes</small><strong>4</strong><i>synced</i></span>
      </div>
      <div className="dashboard-preview__chart">
        <div className="chart-heading">
          <span><small>Application activity</small><strong>One view, every portal</strong></span>
          <span className="chart-change"><ArrowUpRight size={16} aria-hidden="true" /> illustrative</span>
        </div>
        <svg viewBox="0 0 520 210" role="img" aria-label="Illustrative application activity chart">
          <defs>
            <linearGradient id="chartFillTour" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#4d64df" stopOpacity=".25" />
              <stop offset=".54" stopColor="#7252d2" stopOpacity=".1" />
              <stop offset="1" stopColor="#258bc2" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="chartLineTour" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#396fd6" />
              <stop offset=".52" stopColor="#6b4fd0" />
              <stop offset="1" stopColor="#258fc3" />
            </linearGradient>
          </defs>
          <path className="chart-area" fill="url(#chartFillTour)" d="M4 176 C55 169 62 120 110 126 C158 132 164 74 214 88 C264 102 271 142 322 116 C373 89 383 47 430 58 C475 69 492 38 516 26 L516 210 L4 210 Z" />
          <path className="chart-line" stroke="url(#chartLineTour)" d="M4 176 C55 169 62 120 110 126 C158 132 164 74 214 88 C264 102 271 142 322 116 C373 89 383 47 430 58 C475 69 492 38 516 26" />
          {[4, 110, 214, 322, 430, 516].map((x, index) => (
            <circle className="chart-point" cx={x} cy={[176, 126, 88, 116, 58, 26][index]} r="5" key={x} />
          ))}
        </svg>
        <div className="chart-days" aria-hidden="true"><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span></div>
      </div>
    </div>
  );
}

function ApplicationsPane() {
  return (
    <div className="dashboard-tour__applications">
      <div className="dashboard-tour__table-head"><span>Company &amp; role</span><span>Portal</span><span>Status</span></div>
      {applications.map(([company, role, status], index) => (
        <div className="dashboard-tour__row" style={{ "--row-delay": `${index * 70}ms` } as CSSProperties} key={company}>
          <span className={`activity-icon activity-icon--${(index % 4) + 1}`}><BriefcaseBusiness size={16} aria-hidden="true" /></span>
          <span><strong>{company}</strong><small>{role}</small></span>
          <span>{["Darwinbox", "Workday", "PeopleStrong", "Custom ATS", "Greenhouse"][index]}</span>
          <i>{status}</i>
        </div>
      ))}
    </div>
  );
}

function ResumesPane() {
  return (
    <div className="dashboard-tour__resumes">
      <div className="resume-library">
        {[
          ["Frontend · v3", "Northstar", "Role keywords aligned", "coral"],
          ["Product · v2", "Paperplane", "Impact bullets prioritised", "violet"],
          ["Core profile", "Approved source", "Factual baseline locked", "mint"]
        ].map(([name, role, detail, tone], index) => (
          <article className={`resume-file resume-file--${tone}`} style={{ "--row-delay": `${index * 90}ms` } as CSSProperties} key={name}>
            <span><FileText size={22} aria-hidden="true" /></span>
            <div><small>{role}</small><strong>{name}</strong><p>{detail}</p></div>
            <i><CircleCheck size={14} aria-hidden="true" /> checked</i>
          </article>
        ))}
      </div>
      <div className="resume-diff">
        <span><small>Role-tailored change</small><strong>Made React performance work easier to notice.</strong></span>
        <p><del>Worked on frontend performance.</del></p>
        <p><ins>Reduced initial render time by simplifying the React data-loading path.</ins></p>
        <small>Illustrative rewrite · source facts stay locked</small>
      </div>
    </div>
  );
}

function SettingsPane() {
  return (
    <div className="dashboard-tour__settings">
      <div className="settings-profile">
        <span><SlidersHorizontal size={20} aria-hidden="true" /></span>
        <div><small>Agent behaviour</small><strong>Careful, visible, and yours to stop.</strong></div>
      </div>
      {[
        ["Review before every submit", "Always on"],
        ["Applications at once", "1 · sequential"],
        ["Use my saved writing voice", "Approved examples only"],
        ["Flag uncertain answers", "Below confidence threshold"]
      ].map(([label, value]) => (
        <div className="settings-row" key={label}>
          <span><strong>{label}</strong><small>{value}</small></span>
          <i className="is-on"><b /></i>
        </div>
      ))}
      <div className="settings-lock"><ShieldCheck size={15} aria-hidden="true" /> Offr does not invent qualifications or submit past your review gate.</div>
    </div>
  );
}

export function DashboardPreview() {
  const [mode, setMode] = useState<Mode>("overview");
  const { ref, motionActive } = useActiveMotion<HTMLDivElement>(0.12);

  useEffect(() => {
    if (!motionActive) return;
    const timer = window.setInterval(() => {
      setMode((current) => {
        const index = modes.findIndex((item) => item.id === current);
        return modes[(index + 1) % modes.length].id;
      });
    }, 3800);
    return () => window.clearInterval(timer);
  }, [motionActive]);

  return (
    <div className="dashboard-preview" ref={ref}>
      <div className="dashboard-preview__top">
        <strong>Monday, made manageable.</strong>
        <span><i /> Synthetic dashboard preview</span>
      </div>
      <div className="dashboard-tour">
        <aside className="dashboard-tour__nav" aria-label="Dashboard preview views">
          <a className="dashboard-tour__brand" href="#top" aria-label="Offr home"><OffrLogo /></a>
          <nav>
            {modes.map((item) => {
              const Icon = item.icon;
              return (
                <button className={mode === item.id ? "is-active" : ""} type="button" onClick={() => setMode(item.id)} key={item.id}>
                  <Icon size={18} aria-hidden="true" /><span>{item.label}</span>
                </button>
              );
            })}
          </nav>
          <span className="dashboard-tour__agent"><OffrLogo markOnly /><span>Agent ready</span></span>
        </aside>
        <div className="dashboard-tour__main">
          <div className="dashboard-tour__heading">
            <div><small>Dashboard</small><strong>{modes.find((item) => item.id === mode)?.label}</strong></div>
            <span><i /> synced now</span>
          </div>
          <div className={`dashboard-tour__pane dashboard-tour__pane--${mode}`} key={mode}>
            {mode === "overview" && <OverviewPane />}
            {mode === "applications" && <ApplicationsPane />}
            {mode === "resumes" && <ResumesPane />}
            {mode === "settings" && <SettingsPane />}
          </div>
        </div>
      </div>
    </div>
  );
}
